// src/pages/analytics/SocialMediaAnalytics.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  CircularProgress,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Public as SocialIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  ThumbUp as EngagementIcon,
  Group as FollowersIcon,
  Reply as MentionsIcon,
  NavigateNext as NavigateNextIcon,
  FileDownload as DownloadIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as TrendingUpArrowIcon,
  ArrowDownward as TrendingDownArrowIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, PieChart, 
  Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis 
} from 'recharts';

// Import custom components
import MagicCard from '../../components/ui/MagicCard';
import MagicLoading from '../../components/ui/MagicLoading';
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportButton from '../../components/features/analytics/ExportButton';

// Import utilities
import { 
  formatNumber, 
  transformTimeSeriesData, 
  transformDistributionData 
} from '../../utils/analyticsUtils';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportSocialMediaAnalytics } from '../../services/exportService';

// Platform icons map
const PLATFORM_ICONS = {
  facebook: <FacebookIcon />,
  twitter: <TwitterIcon />,
  instagram: <InstagramIcon />,
  linkedin: <LinkedInIcon />,
  youtube: <YouTubeIcon />,
};

/**
 * Social Media Analytics Dashboard Component
 */
const SocialMediaAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [transformedData, setTransformedData] = useState({
    performanceTrend: [],
    platformDistribution: [],
    engagementData: [],
    topPosts: [],
    audienceGrowth: [],
    mentionsData: [],
  });
  
  // Get data from store
  const { 
    socialMediaAnalytics, 
    isLoading, 
    error, 
    fetchSocialMediaAnalytics,
    dateRange,
    setDateRange,
    exportAnalyticsData,
  } = useAnalyticsStore();
  
  const { showError, showSuccess } = useNotificationStore();
  
  // Fetch data when component mounts or filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        // Set loading state
        
        // Prepare params
        const params = {
          platform: platformFilter !== 'all' ? platformFilter : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        };
        
        // Fetch data
        await fetchSocialMediaAnalytics(params);
      } catch (error) {
        showError('Failed to load social media analytics: ' + (error.message || 'Unknown error'));
      }
    };
    
    loadData();
  }, [fetchSocialMediaAnalytics, dateRange, platformFilter, showError]);
  
  // Transform data when analytics changes
  useEffect(() => {
    if (!socialMediaAnalytics) return;
    
    try {
      // Transform performance trend data
      const performanceTrend = transformTimeSeriesData(
        socialMediaAnalytics.performanceTrend || [],
        {
          dateKey: 'date',
          dateFormat: 'short',
          metrics: ['engagement', 'followers', 'reach', 'mentions'],
          sortData: true
        }
      );
      
      // Transform platform distribution
      const platformDistribution = transformDistributionData(
        socialMediaAnalytics.platformDistribution || [],
        {
          nameKey: 'platform',
          valueKey: 'value',
          sortBy: 'value',
          sortDirection: 'desc',
          calculatePercent: true
        }
      );
      
      // Transform engagement data
      const engagementData = transformDistributionData(
        socialMediaAnalytics.engagementData || [],
        {
          nameKey: 'type',
          valueKey: 'count',
          sortBy: 'count',
          sortDirection: 'desc'
        }
      );
      
      // Update state with transformed data
      setTransformedData({
        performanceTrend,
        platformDistribution,
        engagementData,
        topPosts: socialMediaAnalytics.topPosts || [],
        audienceGrowth: socialMediaAnalytics.audienceGrowth || [],
        mentionsData: socialMediaAnalytics.mentionsData || [],
      });
    } catch (error) {
      console.error('Error transforming analytics data:', error);
    }
  }, [socialMediaAnalytics]);
  
  // Handle tab change
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle platform filter change
  const handlePlatformChange = (event) => {
    setPlatformFilter(event.target.value);
  };
  
  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Use the export service
      await exportSocialMediaAnalytics({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        platform: platformFilter !== 'all' ? platformFilter : undefined,
      });
      
      showSuccess(`Analytics report downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  
  // Check if data is available
  const hasData = !isLoading && socialMediaAnalytics;
  
  // Generate demo data for development
  const generateDemoData = () => {
    return {
      overview: {
        followers: {
          total: 128650,
          previousTotal: 120400,
          percentChange: 6.85,
        },
        engagement: {
          total: 45280,
          previousTotal: 40120,
          percentChange: 12.86,
          rate: 3.5,
        },
        mentions: {
          total: 2840,
          previousTotal: 2320,
          percentChange: 22.41,
        },
        posts: {
          total: 120,
          previousTotal: 105,
          percentChange: 14.29,
        },
      },
      platformPerformance: [
        { platform: 'Instagram', followers: 85000, engagement: 25000, engagementRate: 4.2 },
        { platform: 'Facebook', followers: 28000, engagement: 12000, engagementRate: 2.8 },
        { platform: 'Twitter', followers: 15650, engagement: 8280, engagementRate: 3.5 },
        { platform: 'LinkedIn', followers: 7200, engagement: 1500, engagementRate: 1.8 },
        { platform: 'YouTube', followers: 12800, engagement: 4500, engagementRate: 2.5 },
      ],
      performanceTrend: [
        { date: '2023-04-01', engagement: 2500, followers: 110000, reach: 18000, mentions: 120 },
        { date: '2023-04-08', engagement: 3200, followers: 112500, reach: 22000, mentions: 145 },
        { date: '2023-04-15', engagement: 2800, followers: 115000, reach: 20000, mentions: 130 },
        { date: '2023-04-22', engagement: 4500, followers: 118000, reach: 28000, mentions: 180 },
        { date: '2023-04-29', engagement: 3800, followers: 122000, reach: 25000, mentions: 160 },
        { date: '2023-05-06', engagement: 5000, followers: 126000, reach: 30000, mentions: 210 },
        { date: '2023-05-13', engagement: 4800, followers: 128650, reach: 29000, mentions: 205 },
      ],
      platformDistribution: [
        { platform: 'Instagram', value: 55 },
        { platform: 'Facebook', value: 22 },
        { platform: 'Twitter', value: 12 },
        { platform: 'LinkedIn', value: 6 },
        { platform: 'YouTube', value: 5 },
      ],
      engagementData: [
        { type: 'Likes', count: 25000 },
        { type: 'Comments', count: 8500 },
        { type: 'Shares', count: 6200 },
        { type: 'Saves', count: 3800 },
        { type: 'Clicks', count: 12000 },
      ],
      topPosts: [
        {
          id: 'post1',
          content: 'New product launch announcement! #exciting #newproduct',
          platform: 'instagram',
          type: 'photo',
          date: '2023-05-10',
          engagement: 3850,
          reach: 25000,
          engagementRate: 15.4,
        },
        {
          id: 'post2',
          content: 'Behind the scenes look at our team working on the new collection',
          platform: 'facebook',
          type: 'video',
          date: '2023-05-05',
          engagement: 2200,
          reach: 18000,
          engagementRate: 12.2,
        },
        {
          id: 'post3',
          content: 'Customer spotlight: See how our product transformed their business',
          platform: 'linkedin',
          type: 'article',
          date: '2023-04-28',
          engagement: 1540,
          reach: 8500,
          engagementRate: 18.1,
        },
        {
          id: 'post4',
          content: 'Quick tips to maximize productivity with our tools #productivity',
          platform: 'twitter',
          type: 'text',
          date: '2023-05-12',
          engagement: 950,
          reach: 7200,
          engagementRate: 13.2,
        },
      ],
      audienceGrowth: [
        { platform: 'Instagram', followers: 85000, growth: 8.5 },
        { platform: 'Facebook', followers: 28000, growth: 3.2 },
        { platform: 'Twitter', followers: 15650, growth: 5.8 },
        { platform: 'LinkedIn', followers: 7200, growth: 12.5 },
        { platform: 'YouTube', followers: 12800, growth: 15.2 },
      ],
      mentionsData: [
        { platform: 'Twitter', count: 1250, sentiment: 0.68 },
        { platform: 'Instagram', count: 850, sentiment: 0.72 },
        { platform: 'Facebook', count: 450, sentiment: 0.61 },
        { platform: 'LinkedIn', count: 180, sentiment: 0.85 },
        { platform: 'News/Blogs', count: 110, sentiment: 0.58 },
      ],
    };
  };
  
  // Use demo data if no real data is available
  const data = hasData ? socialMediaAnalytics : generateDemoData();
  
  // Loading state
  if (isLoading && !socialMediaAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoading variant="mystical" size="large" message="Loading social media analytics..." />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link 
            color="inherit" 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
            onClick={() => navigate('/analytics')}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Social Media Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Social Media Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
              size="small"
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv', 'json']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Filter bar */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="platform-select-label">Platform</InputLabel>
          <Select
            labelId="platform-select-label"
            id="platform-select"
            value={platformFilter}
            label="Platform"
            onChange={handlePlatformChange}
            startAdornment={<SocialIcon sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="all">All Platforms</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
            <MenuItem value="facebook">Facebook</MenuItem>
            <MenuItem value="twitter">Twitter</MenuItem>
            <MenuItem value="linkedin">LinkedIn</MenuItem>
            <MenuItem value="youtube">YouTube</MenuItem>
          </Select>
        </FormControl>
        
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Overview" />
          <Tab label="Engagement" />
          <Tab label="Top Content" />
          <Tab label="Audience" />
        </Tabs>
      </Box>
      
      {/* Error message */}
      {error && (
        <Card sx={{ p: 2, mb: 4, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}
      
      {/* Overview KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Followers"
            value={data.overview?.followers?.total}
            previousValue={data.overview?.followers?.previousTotal}
            percentChange={data.overview?.followers?.percentChange}
            icon={<FollowersIcon />}
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Engagement"
            value={data.overview?.engagement?.total}
            previousValue={data.overview?.engagement?.previousTotal}
            percentChange={data.overview?.engagement?.percentChange}
            icon={<EngagementIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Mentions"
            value={data.overview?.mentions?.total}
            previousValue={data.overview?.mentions?.previousTotal}
            percentChange={data.overview?.mentions?.percentChange}
            icon={<MentionsIcon />}
            color="info"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Posts"
            value={data.overview?.posts?.total}
            previousValue={data.overview?.posts?.previousTotal}
            percentChange={data.overview?.posts?.percentChange}
            icon={<ShareIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
      </Grid>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Performance Trend Chart */}
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Performance Trends"
              subtitle="Key metrics over time"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={hasData ? transformedData.performanceTrend : data.performanceTrend}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="followers"
                    name="Followers"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="engagement"
                    name="Engagement"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="mentions"
                    name="Mentions"
                    stroke={theme.palette.info.main}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          {/* Platform Distribution */}
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Platform Distribution"
              subtitle="Engagement by platform"
              icon={<SocialIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={hasData ? transformedData.platformDistribution : data.platformDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="platform"
                    label={(entry) => `${entry.platform}: ${formatNumber(entry.percent || entry.value, 'decimal', {digits: 1})}%`}
                    labelLine={false}
                  >
                    {(hasData ? transformedData.platformDistribution : data.platformDistribution).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={chartColors[index % chartColors.length]} 
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name, props) => [
                      `${formatNumber(props.payload.percent || props.payload.value, 'decimal', {digits: 1})}%`, 
                      props.payload.platform
                    ]}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          {/* Platform Performance Table */}
          <Grid item xs={12}>
            <ChartCard
              title="Platform Performance"
              subtitle="Key metrics by platform"
              icon={<SocialIcon />}
              isLoading={isLoading}
              height={430}
              
            >
              <TableContainer sx={{ maxHeight: 370 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell align="right">Followers</TableCell>
                      <TableCell align="right">Engagement</TableCell>
                      <TableCell align="right">Engagement Rate</TableCell>
                      <TableCell align="right">Growth</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.platformPerformance?.map((row) => (
                      <TableRow
                        key={row.platform}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                mr: 2, 
                                bgcolor: row.platform.toLowerCase() === 'instagram' 
                                  ? '#E1306C' 
                                  : row.platform.toLowerCase() === 'facebook'
                                  ? '#1877F2'
                                  : row.platform.toLowerCase() === 'twitter'
                                  ? '#1DA1F2'
                                  : row.platform.toLowerCase() === 'linkedin'
                                  ? '#0077B5'
                                  : '#FF0000' // YouTube
                              }}
                            >
                              {PLATFORM_ICONS[row.platform.toLowerCase()] || <SocialIcon />}
                            </Avatar>
                            {row.platform}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.followers, 'compact')}</TableCell>
                        <TableCell align="right">{formatNumber(row.engagement, 'compact')}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${row.engagementRate}%`}
                            color={
                              row.engagementRate > 3 
                                ? 'success' 
                                : row.engagementRate > 1.5 
                                ? 'primary' 
                                : 'secondary'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-end',
                            color: data.audienceGrowth.find(p => p.platform === row.platform)?.growth > 0 
                              ? theme.palette.success.main 
                              : theme.palette.error.main
                          }}>
                            {data.audienceGrowth.find(p => p.platform === row.platform)?.growth > 0 
                              ? <TrendingUpArrowIcon fontSize="small" sx={{ mr: 0.5 }} />
                              : <TrendingDownArrowIcon fontSize="small" sx={{ mr: 0.5 }} />
                            }
                            {data.audienceGrowth.find(p => p.platform === row.platform)?.growth}%
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Engagement Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Engagement Breakdown */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Engagement Breakdown"
              subtitle="Types of engagement"
              icon={<EngagementIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={hasData ? transformedData.engagementData : data.engagementData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    type="number"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    dataKey="type"
                    type="category"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [formatNumber(value, 'decimal', {digits: 0})]}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    name="Count" 
                    fill={theme.palette.secondary.main}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          {/* Mentions Data */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Brand Mentions"
              subtitle="Mentions across platforms"
              icon={<MentionsIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart 
                  cx="50%" 
                  cy="50%" 
                  outerRadius="80%" 
                  data={data.mentionsData}
                >
                  <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                  <PolarAngleAxis 
                    dataKey="platform" 
                    tick={{ fill: theme.palette.text.secondary }}
                  />
                  <PolarRadiusAxis 
                    tick={{ fill: theme.palette.text.secondary }}
                    angle={30}
                  />
                  <Radar 
                    name="Mentions" 
                    dataKey="count" 
                    stroke={theme.palette.primary.main} 
                    fill={alpha(theme.palette.primary.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Radar 
                    name="Sentiment (0-100)" 
                    dataKey="sentiment" 
                    stroke={theme.palette.success.main} 
                    fill={alpha(theme.palette.success.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Legend />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      if (name === 'Sentiment (0-100)') {
                        return [`${(value * 100).toFixed(0)}%`];
                      }
                      return [formatNumber(value, 'decimal', {digits: 0})];
                    }}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          {/* Audience Growth */}
          <Grid item xs={12}>
            <ChartCard
              title="Audience Growth"
              subtitle="Follower growth by platform"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={data.audienceGrowth}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="platform"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left"
                    dataKey="followers" 
                    name="Total Followers" 
                    fill={theme.palette.primary.main}
                    barSize={20}
                  />
                  <Bar 
                    yAxisId="right"
                    dataKey="growth" 
                    name="Growth %" 
                    fill={theme.palette.success.main}
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Top Content Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Top Performing Content"
              subtitle="Posts with highest engagement"
              icon={<ShareIcon />}
              isLoading={isLoading}
              height={500}
              
            >
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Content</TableCell>
                      <TableCell align="center">Platform</TableCell>
                      <TableCell align="center">Type</TableCell>
                      <TableCell align="right">Date</TableCell>
                      <TableCell align="right">Engagement</TableCell>
                      <TableCell align="right">Engagement Rate</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.topPosts?.map((post) => (
                      <TableRow
                        key={post.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
                            {post.content}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Avatar 
                            sx={{ 
                              bgcolor: post.platform.toLowerCase() === 'instagram' 
                                ? '#E1306C' 
                                : post.platform.toLowerCase() === 'facebook'
                                ? '#1877F2'
                                : post.platform.toLowerCase() === 'twitter'
                                ? '#1DA1F2'
                                : post.platform.toLowerCase() === 'linkedin'
                                ? '#0077B5'
                                : '#FF0000', // YouTube
                              mx: 'auto'
                            }}
                          >
                            {PLATFORM_ICONS[post.platform.toLowerCase()] || <SocialIcon />}
                          </Avatar>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={post.type}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {new Date(post.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">{formatNumber(post.engagement, 'compact')}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${post.engagementRate}%`}
                            color={
                              post.engagementRate > 10 
                                ? 'success' 
                                : post.engagementRate > 5 
                                ? 'primary' 
                                : 'secondary'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Audience Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Audience demographics coming soon
            </Typography>
            <Typography variant="body1">
              We're working on integrating detailed audience demographics for your social media profiles.
              This feature will provide insights into age, gender, location, and interests of your audience.
            </Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SocialMediaAnalytics;